<template>
  <v-dialog
    v-model="open"
    persistent
    max-width="800px"
    content-class="video-dialog"
    @input="handleDialogChange"
  >
    <div class="video-wrapper">
      <!-- YouTube player -->
      <template v-if="article.videoOrigin === 'youtube'">
        <div class="video-player-container">
          <div class="plyr__video-embed">
            <iframe
              id="youtube-player"
              :src="
                `https://www.youtube.com/embed/${article.videoUrl}?origin=${origin}&iv_load_policy=3&modestbranding=1&playsinline=1&showinfo=0&rel=0&enablejsapi=1&disablekb=1&fs=1&color=white&controls=1&hl=en&cc_load_policy=0`
              "
              allowfullscreen
              allowtransparency
              allow="autoplay"
              frameborder="0"
              referrerpolicy="no-referrer"
            ></iframe>
          </div>
        </div>
      </template>

      <!-- Vimeo player -->
      <template v-else-if="article.videoUrl && article.videoOrigin === 'vimeo'">
        <div class="video-player-container">
          <iframe
            id="vimeo-player"
            :src="
              `https://player.vimeo.com/video/${article.videoUrl}?h=00000000&badge=0&autopause=0&player_id=0&app_id=58479&byline=0&portrait=0&title=0&transparent=1&dnt=1`
            "
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            title="Vimeo Video"
          ></iframe>
        </div>
      </template>

      <!-- Incentable player -->
      <template v-else-if="article.videoOrigin === 'incentable'">
        <div class="video-player-container incentable-container">
          <video
            id="incentable-player"
            controls
            preload="metadata"
            :src="article.videoUrl"
            title="Incentable Video"
            @ended="handleVideoComplete"
            @timeupdate="handleTimeUpdate"
            @loadedmetadata="handleVideoLoaded"
            @seeking="handleSeeking"
            class="incentable-video"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </template>

      <!-- Fallback placeholder -->
      <template v-else>
        <div class="video-fallback">
          <v-icon color="primary" size="80">play_circle_filled</v-icon>
        </div>
      </template>

      <!-- Close button -->
      <v-btn
        class="close-button"
        color="white"
        @click="closeArticleVideoPopUp()"
        fab
        x-small
      >
        <v-icon color="black" size="20">clear</v-icon>
      </v-btn>

      <!-- Debug indicator (only visible when debug mode is on) -->
      <div v-if="debugMode" class="debug-indicator">
        <v-chip color="red" text-color="white" small>
          DEBUG: {{ article.videoOrigin }} - {{ Math.floor(percentWatched) }}%
        </v-chip>
      </div>
    </div>
  </v-dialog>
</template>

<script>
// debug mode is false by default
//
// HOW TO ENABLE DEBUG MODE:
// 1. Change debugMode to true in the data() function (around line 170)
// 2. Or temporarily enable it by changing line 123: this.debugMode = true;
// 3. Or enable it via console: window.videoDialogComponent.debugMode = true
//
// AVAILABLE DEBUG TOOLS:
// - window.videoDialogComponent.testSnackbar() - Test points eligibility
// - window.videoDialogComponent.manualVideoSetup() - Manually setup video tracking
// - window.videoDialogComponent.manualVideoEnd() - Manually trigger video end
// - window.videoDialogComponent.manualPlay() - Manually play YouTube video
// - window.videoDialogComponent.checkIframe() - Check iframe status
// - window.videoDialogComponent.debugMode = true - Enable debug logging
//
// DEBUG FEATURES WHEN ENABLED:
// - Red debug chip showing video type and completion percentage
// - Extensive console logging with emoji indicators
// - Progress updates every 5 seconds during playback
// - Detailed eligibility checking information
// - Manual testing methods accessible via console
//
export default {
  components: {},
  props: ["open", "onClose", "article"],
  created() {
    // Immediate debug logging
    console.log("🎬 ArticleDialogVideo component created");
    console.log("📄 Article data:", this.article);
    console.log("🔓 Open prop:", this.open);
  },
  mounted() {
    // Expose component to global scope for debugging
    if (typeof window !== "undefined") {
      window.videoDialogComponent = this;
      console.log(
        "🎬 ArticleDialogVideo component mounted and exposed to window.videoDialogComponent"
      );
    }

    // Log initial state
    console.log("🎬 Component mounted with:", {
      open: this.open,
      article: this.article,
      videoOrigin: this.article?.videoOrigin,
      videoUrl: this.article?.videoUrl,
      watchPoints: this.article?.watchPoints
    });

    // If dialog is already open when component is mounted, trigger setup
    if (this.open) {
      console.log("🎬 Dialog is already open on mount, triggering setup...");

      // Enable debug mode for YouTube videos to help troubleshoot
      this.debugMode = false;

      if (this.debugMode) {
        console.log(
          "🎬 Debug mode enabled for YouTube video:",
          this.article.title
        );
      }

      // Check if user has already watched this article
      this.checkWatchStatus();
      // Reset tracking data
      this.resetTrackingData();

      // Trigger video setup
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.debugMode) {
            console.log("🔄 Mounted setup trigger");
          }
          this.setupVideoTracking();
        }, 300);
      });
    }
  },
  beforeDestroy() {
    // Clean up intervals when component is destroyed
    clearInterval(this.youtubeTrackingInterval);
    clearInterval(this.vimeoTrackingInterval);
    // Clean up YouTube player reference
    if (this.youtubePlayer) {
      this.youtubePlayer = null;
    }
  },
  data: function() {
    const initialData = {
      origin: window.location.origin,
      videoDuration: 0,
      currentTime: 0,
      lastTimeUpdate: 0,
      watchPoints: 10, // Example points value - for display only
      hasWatched95Percent: false,
      watchLog: [], // To track watching patterns
      youtubeTrackingInterval: null,
      vimeoTrackingInterval: null,
      hasAlreadyWatched: false, // Track if user has already watched this article
      seekingCount: 0, // Track number of seeking events
      maxAllowedSeeks: 8, // Increased from 5 to be less restrictive
      fastForwardDetected: false, // Flag for when fast-forwarding is detected
      watchPercentageThreshold: 95, // Percentage required to earn points
      continuousWatchSegments: [], // Track continuous watch segments
      timeJumps: [], // Track time jumps for analysis
      watchedTimeRanges: new Set(), // Track unique second-level ranges watched
      eligibleForPoints: true, // Track if user is eligible for points
      youtubePlayer: null, // Store YouTube player reference
      youtubeApiReady: false, // Track if YouTube API is ready
      debugMode: false // Enable debug logging
    };

    console.log("🎬 ArticleDialogVideo data initialized");
    return initialData;
  },
  computed: {
    programId() {
      return this.$store.getters.programId;
    },
    currentMemberId() {
      return this.$store.getters.currentUser._id;
    },
    webTheme() {
      return this.$store.getters.currentProgram.webTheme;
    },
    percentWatched() {
      if (!this.videoDuration) return 0;
      return (this.currentTime / this.videoDuration) * 100;
    },
    totalUniqueSecondsWatched() {
      return this.watchedTimeRanges.size;
    },
    requiredUniqueSeconds() {
      // For videos under 30 seconds, require 80% instead of 95%
      // For longer videos, use 90% instead of 95%
      const percentage = this.videoDuration < 30 ? 80 : 90;
      return Math.floor(this.videoDuration * (percentage / 100));
    },
    genuinelyWatched() {
      // User must have watched enough unique seconds and not fast-forwarded excessively
      const result =
        this.totalUniqueSecondsWatched >= this.requiredUniqueSeconds &&
        this.seekingCount <= this.maxAllowedSeeks &&
        !this.fastForwardDetected &&
        this.eligibleForPoints;

      if (this.debugMode) {
        console.log("🎯 Genuinely Watched Check:", {
          totalUniqueSecondsWatched: this.totalUniqueSecondsWatched,
          requiredUniqueSeconds: this.requiredUniqueSeconds,
          seekingCount: this.seekingCount,
          maxAllowedSeeks: this.maxAllowedSeeks,
          fastForwardDetected: this.fastForwardDetected,
          eligibleForPoints: this.eligibleForPoints,
          result: result
        });
      }

      return result;
    }
  },
  watch: {
    open(newVal) {
      if (newVal) {
        // Enable debug mode for YouTube videos to help troubleshoot
        this.debugMode = false;

        if (this.debugMode) {
          console.log(
            "🎬 Dialog opening for YouTube video:",
            this.article.title
          );
        }

        // Check if user has already watched this article when the dialog opens
        this.checkWatchStatus();
        // Reset tracking data when dialog opens
        this.resetTrackingData();

        // Also trigger video setup directly here as backup
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.debugMode) {
              console.log("🔄 Backup video setup trigger");
            }
            this.setupVideoTracking();
          }, 300);
        });
      }
    }
  },
  methods: {
    resetTrackingData() {
      this.currentTime = 0;
      this.videoDuration = 0;
      this.hasWatched95Percent = false;
      this.watchLog = [];
      this.seekingCount = 0;
      this.fastForwardDetected = false;
      this.lastTimeUpdate = Date.now();
      this.continuousWatchSegments = [];
      this.timeJumps = [];
      this.watchedTimeRanges = new Set();
      this.eligibleForPoints = true;

      // Enable debug mode for YouTube videos to help troubleshoot
      this.debugMode = false;

      if (this.debugMode) {
        console.log(
          "🔄 Tracking data reset for YouTube video:",
          this.article.title
        );
      }
    },

    handleSeeking(event) {
      // Get current and previous time
      const newTime = event.target.currentTime;
      const previousTime = this.currentTime;

      // Calculate the jump size
      const jumpSize = Math.abs(newTime - previousTime);

      // Record the jump if it's significant (more than 2 seconds)
      if (jumpSize > 2) {
        this.seekingCount++;
        this.timeJumps.push({
          from: previousTime,
          to: newTime,
          jumpSize: jumpSize,
          timestamp: Date.now()
        });

        // If user is seeking forward significantly, flag it
        if (newTime > previousTime && jumpSize > 10) {
          // If seeking forward more than 10 seconds
          if (this.seekingCount > this.maxAllowedSeeks) {
            this.fastForwardDetected = true;
            this.eligibleForPoints = false;
          }
        }
      }
    },

    trackUniqueTimeRange(time) {
      // Track each second watched as a unique integer
      this.watchedTimeRanges.add(Math.floor(time));
    },

    async checkWatchStatus() {
      if (!this.article.id || !this.currentMemberId || !this.programId) {
        return;
      }

      try {
        const hasWatched = await this.$store.dispatch(
          "checkArticleWatchStatus",
          {
            programId: this.programId,
            articleId: this.article.id,
            memberId: this.currentMemberId
          }
        );

        this.hasAlreadyWatched = hasWatched;
      } catch (error) {
        // Default to false to potentially prevent duplicate points
        this.hasAlreadyWatched = false;
      }
    },

    async recordWatchCompletion() {
      if (this.hasAlreadyWatched) {
        return false;
      }

      if (!this.article.id || !this.currentMemberId || !this.programId) {
        return false;
      }

      try {
        // Check again to make sure we're not duplicating points
        const checkResult = await this.$store.dispatch(
          "checkArticleWatchStatus",
          {
            programId: this.programId,
            articleId: this.article.id,
            memberId: this.currentMemberId
          }
        );

        if (checkResult) {
          this.hasAlreadyWatched = true;
          return false;
        }

        const success = await this.$store.dispatch("recordArticleWatch", {
          programId: this.programId,
          articleId: this.article.id,
          memberId: this.currentMemberId,
          title: this.article.title || "",
          pointsAwarded: Number(this.article.watchPoints) || 0
        });

        if (success) {
          this.hasAlreadyWatched = true;
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    },
    testSnackbar() {
      const debugInfo = {
        videoOrigin: this.article.videoOrigin,
        videoDuration: this.videoDuration,
        currentTime: this.currentTime,
        percentWatched: Math.floor(this.percentWatched),
        totalUniqueSecondsWatched: this.totalUniqueSecondsWatched,
        requiredUniqueSeconds: this.requiredUniqueSeconds,
        seekingCount: this.seekingCount,
        maxAllowedSeeks: this.maxAllowedSeeks,
        fastForwardDetected: this.fastForwardDetected,
        eligibleForPoints: this.eligibleForPoints,
        genuinelyWatched: this.genuinelyWatched,
        hasAlreadyWatched: this.hasAlreadyWatched,
        watchPoints: this.article.watchPoints,
        youtubeApiReady: this.youtubeApiReady,
        youtubePlayer: !!this.youtubePlayer,
        debugMode: this.debugMode
      };

      console.log("🧪 Video Debug Info:", debugInfo);

      this.$store.dispatch(
        "setSnackbar",
        `Debug: ${this.genuinelyWatched ? "Eligible" : "Not eligible"} for ${
          this.article.watchPoints
        } points`
      );
    },
    closeArticleVideoPopUp() {
      this.$emit("onClose");
    },
    handleVideoComplete() {
      // First, check if user has already watched/earned points
      this.checkWatchStatus().then(() => {
        // If user has already earned points, don't show any messages
        if (this.hasAlreadyWatched) {
          return;
        }

        // Only award points if user has genuinely watched the video
        if (this.genuinelyWatched) {
          if (this.debugMode) {
            console.log("🎉 User eligible for points!");
          }

          // Show message and award points
          const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
          this.$store.dispatch("setSnackbar", message);

          // Add points to member account
          this.recordWatchCompletion().then(recorded => {
            if (recorded) {
              this.awardWatchPoints();
              if (this.debugMode) {
                console.log("✅ Points awarded successfully");
              }
            } else {
              if (this.debugMode) {
                console.log("❌ Failed to record watch completion");
              }
            }
          });
        } else if (
          this.percentWatched >= 95 &&
          this.totalUniqueSecondsWatched >= this.requiredUniqueSeconds * 0.7
        ) {
          // Fallback: If user watched 95% of video and at least 70% of required unique seconds
          if (this.debugMode) {
            console.log("🎉 User eligible for points via fallback condition!");
          }

          const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
          this.$store.dispatch("setSnackbar", message);

          this.recordWatchCompletion().then(recorded => {
            if (recorded) {
              this.awardWatchPoints();
              if (this.debugMode) {
                console.log("✅ Points awarded successfully (fallback)");
              }
            }
          });
        } else if (this.percentWatched >= 90) {
          // Additional fallback: If user watched 90%+ of the video, award points
          // This handles cases where time tracking might have issues
          if (this.debugMode) {
            console.log(
              "🎉 User eligible for points via high completion fallback (90%+)!"
            );
          }

          const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
          this.$store.dispatch("setSnackbar", message);

          this.recordWatchCompletion().then(recorded => {
            if (recorded) {
              this.awardWatchPoints();
              if (this.debugMode) {
                console.log(
                  "✅ Points awarded successfully (high completion fallback)"
                );
              }
            }
          });
        } else if (this.percentWatched >= 95) {
          // If they've reached 95% but through skipping
          if (this.debugMode) {
            console.log(
              "⚠️ User reached 95% but not eligible due to insufficient unique seconds"
            );
          }
          this.$store.dispatch(
            "setSnackbar",
            "Please watch the video without skipping to earn points."
          );
        } else {
          if (this.debugMode) {
            console.log("ℹ️ User did not watch enough of the video");
          }
        }
      });
    },
    awardWatchPoints() {
      // Get current user ID from store
      const memberId = this.$store.getters.currentUser._id;

      if (!memberId) {
        if (this.debugMode) {
          console.error("❌ No member ID found for point awarding");
        }
        return;
      }

      const payload = {
        points: Number(this.article.watchPoints),
        type: "Award",
        notes: `${this.article.title || "Untitled video"}`,
        description: "Video Points",
        memberId: memberId
      };

      if (this.debugMode) {
        console.log("💰 Awarding points with payload:", payload);
      }

      this.$store
        .dispatch("addMemberPoints", payload)
        .then(result => {
          if (this.debugMode) {
            console.log("✅ Points dispatch result:", result);
          }
        })
        .catch(error => {
          if (this.debugMode) {
            console.error("❌ Error dispatching points:", error);
          }
        });
    },
    handleTimeUpdate(event) {
      const now = Date.now();
      const timeDiff = now - this.lastTimeUpdate;
      const previousTime = this.currentTime;
      this.currentTime = event.target.currentTime;

      // Check if user has restarted the video - if they're back to the beginning
      if (previousTime > 10 && this.currentTime < 3) {
        // Reset validation flags for a new viewing
        this.resetValidationState();
      }

      // Track all seconds between previous time and current time
      // This ensures we don't miss any seconds due to timing intervals
      const startSecond = Math.floor(previousTime);
      const endSecond = Math.floor(this.currentTime);

      // Always track the current second
      this.trackUniqueTimeRange(this.currentTime);

      // If there's a gap, fill in all the seconds between
      if (endSecond > startSecond && endSecond - startSecond <= 5) {
        // Only fill gaps up to 5 seconds to avoid counting skipped content
        for (let second = startSecond + 1; second <= endSecond; second++) {
          this.watchedTimeRanges.add(second);
        }
      }

      // Also track a range around the current time to be more forgiving
      const currentSecond = Math.floor(this.currentTime);
      const previousSecond = Math.floor(previousTime);

      // If we're progressing normally (within 2 seconds), track both seconds
      if (Math.abs(currentSecond - previousSecond) <= 2) {
        this.watchedTimeRanges.add(previousSecond);
        this.watchedTimeRanges.add(currentSecond);
      }

      // Record the update in the watch log for analysis
      this.watchLog.push({
        time: this.currentTime,
        timestamp: now,
        timeSinceLastUpdate: timeDiff
      });

      // Update the last time we received an update
      this.lastTimeUpdate = now;

      // If there was a jump larger than expected for normal playback
      // (Allow for a small margin of error), we consider it fast-forwarding
      const expectedTimeDiff = timeDiff / 1000; // Convert milliseconds to seconds
      const actualTimeDiff = this.currentTime - previousTime;

      // Detect abnormal playback
      const isNormalPlayback =
        actualTimeDiff <= expectedTimeDiff * 1.5 && actualTimeDiff >= 0;

      // If playback speed is much faster than real-time, flag as fast-forwarding
      if (actualTimeDiff > 0 && actualTimeDiff > expectedTimeDiff * 2) {
        this.timeJumps.push({
          from: previousTime,
          to: this.currentTime,
          jumpSize: actualTimeDiff,
          expectedDiff: expectedTimeDiff,
          timestamp: now
        });

        // If we detect multiple fast-forward events, mark the user ineligible
        if (this.timeJumps.length > 3) {
          this.fastForwardDetected = true;
          this.eligibleForPoints = false;
        }
      }

      // Update the continuous watching segments
      if (this.continuousWatchSegments.length === 0) {
        // First segment
        this.continuousWatchSegments.push({
          start: this.currentTime,
          end: this.currentTime
        });
      } else {
        // Update the end of the last segment if continuous
        const lastSegment = this.continuousWatchSegments[
          this.continuousWatchSegments.length - 1
        ];

        if (
          isNormalPlayback &&
          Math.abs(this.currentTime - lastSegment.end) < 2
        ) {
          lastSegment.end = this.currentTime;
        } else {
          // Start a new segment
          this.continuousWatchSegments.push({
            start: this.currentTime,
            end: this.currentTime
          });
        }
      }

      // Check if we've reached 95% with genuine watching
      if (
        this.percentWatched >= this.watchPercentageThreshold &&
        this.totalUniqueSecondsWatched >= this.requiredUniqueSeconds &&
        !this.fastForwardDetected
      ) {
        this.hasWatched95Percent = true;
      }

      if (
        this.debugMode &&
        Math.floor(this.currentTime) % 5 === 0 &&
        Math.floor(this.currentTime) !== Math.floor(previousTime)
      ) {
        console.log("📊 Progress update:", {
          currentTime: Math.floor(this.currentTime),
          percentWatched: Math.floor(this.percentWatched),
          uniqueSecondsWatched: this.totalUniqueSecondsWatched,
          requiredSeconds: this.requiredUniqueSeconds,
          seekingCount: this.seekingCount,
          eligibleForPoints: this.eligibleForPoints,
          watchedRanges: Array.from(this.watchedTimeRanges).sort(
            (a, b) => a - b
          )
        });
      }
    },
    handleVideoLoaded(event) {
      this.videoDuration = event.target.duration;
      this.currentTime = 0;
      this.hasWatched95Percent = false;
      this.watchLog = [];
      this.lastTimeUpdate = Date.now();
      this.continuousWatchSegments = [];
      this.timeJumps = [];
      this.watchedTimeRanges = new Set();
      this.seekingCount = 0;
      this.fastForwardDetected = false;
      this.eligibleForPoints = true;

      // Reset validation state when video is loaded
      this.resetValidationState();
    },
    setupVideoTracking() {
      // Reset tracking state
      this.resetTrackingData();

      // Also reset validation state for a fresh viewing
      this.resetValidationState();

      if (this.debugMode) {
        console.log(
          "🎬 Setting up video tracking for:",
          this.article.videoOrigin
        );
      }

      // Setup based on video type
      if (this.article.videoOrigin === "youtube") {
        // Add a small delay to ensure the iframe is properly loaded
        setTimeout(() => {
          this.setupYouTubeTracking();
        }, 100);
      } else if (this.article.videoOrigin === "vimeo") {
        this.setupVimeoTracking();
      }
      // Note: Incentable videos are already tracked via direct event handlers
    },

    setupYouTubeTracking() {
      if (this.debugMode) {
        console.log("🎬 Setting up YouTube tracking...");
      }

      // Check if iframe exists and is visible
      const iframe = document.getElementById("youtube-player");
      if (!iframe) {
        if (this.debugMode) {
          console.error("❌ YouTube iframe not found, retrying in 500ms...");
        }
        setTimeout(() => this.setupYouTubeTracking(), 500);
        return;
      }

      if (this.debugMode) {
        console.log("✅ YouTube iframe found:", {
          src: iframe.src,
          offsetWidth: iframe.offsetWidth,
          offsetHeight: iframe.offsetHeight
        });
      }

      // Load YouTube API if not already loaded
      if (!window.YT) {
        if (this.debugMode) {
          console.log("📥 Loading YouTube API...");
        }

        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        // Set up the callback for when API is ready
        const originalCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          // Call original callback if it exists
          if (originalCallback && typeof originalCallback === "function") {
            try {
              originalCallback();
            } catch (e) {
              // Ignore errors from other callbacks
            }
          }

          this.youtubeApiReady = true;
          if (this.debugMode) {
            console.log("✅ YouTube API loaded and ready");
          }
          this.initYouTubePlayer();
        };

        // Also set up a polling fallback in case the callback doesn't work
        let attempts = 0;
        const checkApiReady = () => {
          attempts++;
          if (window.YT && window.YT.Player) {
            this.youtubeApiReady = true;
            if (this.debugMode) {
              console.log(
                `✅ YouTube API ready via polling (attempt ${attempts})`
              );
            }
            this.initYouTubePlayer();
          } else if (attempts < 20) {
            setTimeout(checkApiReady, 500);
          } else {
            if (this.debugMode) {
              console.error("❌ YouTube API failed to load after 10 seconds");
            }
          }
        };
        setTimeout(checkApiReady, 1000);
      } else if (window.YT && window.YT.Player) {
        this.youtubeApiReady = true;
        if (this.debugMode) {
          console.log("✅ YouTube API already available");
        }
        this.initYouTubePlayer();
      } else {
        // API exists but might not be fully ready
        if (this.debugMode) {
          console.log("⏳ YouTube API exists but not ready, waiting...");
        }
        setTimeout(() => this.setupYouTubeTracking(), 500);
      }
    },

    initYouTubePlayer() {
      const frameId = "youtube-player";
      const frame = document.getElementById(frameId);

      if (!frame) {
        if (this.debugMode) {
          console.error("❌ YouTube iframe not found:", frameId);
        }
        return;
      }

      if (!window.YT || !window.YT.Player) {
        if (this.debugMode) {
          console.error("❌ YouTube API not available");
        }
        return;
      }

      // Prevent duplicate initialization
      if (this.youtubePlayer) {
        if (this.debugMode) {
          console.log(
            "⚠️ YouTube player already exists, skipping initialization"
          );
        }
        return;
      }

      try {
        if (this.debugMode) {
          console.log("🎯 Initializing YouTube player...");
        }

        // Create new player
        this.youtubePlayer = new YT.Player(frameId, {
          events: {
            onReady: event => {
              if (this.debugMode) {
                console.log("🎬 YouTube player ready");
              }

              try {
                this.videoDuration = event.target.getDuration();
                if (this.debugMode) {
                  console.log("⏱️ Video duration:", this.videoDuration);
                }
                // Reset validation state when video is ready to play
                this.resetValidationState();
              } catch (error) {
                if (this.debugMode) {
                  console.error("❌ Error getting video duration:", error);
                }
              }
            },
            onStateChange: event => {
              if (this.debugMode) {
                console.log("🎵 YouTube state change:", event.data);
              }
              this.handleYouTubeStateChange(event);
            },
            onError: event => {
              if (this.debugMode) {
                console.error("❌ YouTube player error:", event.data);
              }
            }
          }
        });
      } catch (error) {
        if (this.debugMode) {
          console.error("❌ Error initializing YouTube player:", error);
        }
      }
    },

    handleYouTubeStateChange(event) {
      const player = this.youtubePlayer;
      if (!player) return;

      try {
        // YT.PlayerState.PLAYING = 1
        if (event.data === 1) {
          if (this.debugMode) {
            console.log("▶️ YouTube video started playing");
          }

          // Check if this is a re-play from the beginning
          const currentTime = player.getCurrentTime();
          if (Math.floor(currentTime) <= 3) {
            // If user starts from beginning, reset validation
            this.resetValidationState();
            if (this.debugMode) {
              console.log("🔄 Reset validation - playing from beginning");
            }
          }

          // Clear any existing interval
          if (this.youtubeTrackingInterval) {
            clearInterval(this.youtubeTrackingInterval);
          }

          // Start tracking when playing
          this.youtubeTrackingInterval = setInterval(() => {
            this.trackYouTubeProgress(player);
          }, 500);
        } else if (event.data === 0) {
          // YT.PlayerState.ENDED = 0
          if (this.debugMode) {
            console.log("🏁 YouTube video ended");
          }

          // Clear interval when video ends
          clearInterval(this.youtubeTrackingInterval);
          this.handleVideoEnded();
        } else if (event.data === 2) {
          // YT.PlayerState.PAUSED = 2
          if (this.debugMode) {
            console.log("⏸️ YouTube video paused");
          }

          // Clear interval when video is paused
          clearInterval(this.youtubeTrackingInterval);
        } else if (event.data === 3) {
          // YT.PlayerState.BUFFERING = 3
          if (this.debugMode) {
            console.log("⏳ YouTube video buffering");
          }

          // Don't treat buffering as seeking - this was a bug in the original code
          // Buffering is normal and shouldn't penalize the user
        } else if (event.data === 5) {
          // YT.PlayerState.CUED = 5
          if (this.debugMode) {
            console.log("📋 YouTube video cued");
          }
        }
      } catch (error) {
        if (this.debugMode) {
          console.error("❌ Error handling YouTube state change:", error);
        }
      }
    },

    trackYouTubeProgress(player) {
      try {
        const now = Date.now();
        const timeDiff = now - this.lastTimeUpdate;
        const previousTime = this.currentTime;
        const newTime = player.getCurrentTime();

        // Track all seconds between previous time and current time
        // This ensures we don't miss any seconds due to timing intervals
        const startSecond = Math.floor(previousTime);
        const endSecond = Math.floor(newTime);

        // Always track the current second
        this.trackUniqueTimeRange(newTime);

        // If there's a gap, fill in all the seconds between
        if (endSecond > startSecond && endSecond - startSecond <= 5) {
          // Only fill gaps up to 5 seconds to avoid counting skipped content
          for (let second = startSecond + 1; second <= endSecond; second++) {
            this.watchedTimeRanges.add(second);
          }
        }

        // Also track a range around the current time to be more forgiving
        const currentSecond = Math.floor(newTime);
        const previousSecond = Math.floor(previousTime);

        // If we're progressing normally (within 2 seconds), track both seconds
        if (Math.abs(currentSecond - previousSecond) <= 2) {
          this.watchedTimeRanges.add(previousSecond);
          this.watchedTimeRanges.add(currentSecond);
        }

        // Record the update
        this.watchLog.push({
          time: newTime,
          timestamp: now,
          timeSinceLastUpdate: timeDiff
        });

        // Detect if user is fast-forwarding (but be more lenient)
        const expectedTimeDiff = timeDiff / 1000;
        const actualTimeDiff = newTime - previousTime;

        // Allow for some variance in timing
        const isNormalPlayback =
          actualTimeDiff <= expectedTimeDiff * 2 && actualTimeDiff >= -1;

        // Only flag as fast-forwarding if the jump is very significant
        if (
          actualTimeDiff > 0 &&
          actualTimeDiff > expectedTimeDiff * 3 &&
          actualTimeDiff > 5
        ) {
          this.timeJumps.push({
            from: previousTime,
            to: newTime,
            jumpSize: actualTimeDiff,
            expectedDiff: expectedTimeDiff,
            timestamp: now
          });

          if (this.debugMode) {
            console.log("⚠️ Potential fast-forward detected:", {
              from: previousTime,
              to: newTime,
              jumpSize: actualTimeDiff,
              expectedDiff: expectedTimeDiff
            });
          }

          // Only mark as ineligible if there are many large jumps
          if (this.timeJumps.filter(jump => jump.jumpSize > 10).length > 5) {
            this.fastForwardDetected = true;
            this.eligibleForPoints = false;
            if (this.debugMode) {
              console.log(
                "❌ Too many fast-forwards detected, marking ineligible"
              );
            }
          }
        }

        // Update continuous watching segments
        if (this.continuousWatchSegments.length === 0) {
          // First segment
          this.continuousWatchSegments.push({
            start: newTime,
            end: newTime
          });
        } else {
          // Update the end of the last segment if continuous
          const lastSegment = this.continuousWatchSegments[
            this.continuousWatchSegments.length - 1
          ];

          if (isNormalPlayback && Math.abs(newTime - lastSegment.end) < 3) {
            lastSegment.end = newTime;
          } else {
            // Start a new segment
            this.continuousWatchSegments.push({
              start: newTime,
              end: newTime
            });
          }
        }

        // Update current time
        this.currentTime = newTime;
        this.lastTimeUpdate = now;

        // Check if we've reached 95% with genuine watching
        if (
          this.percentWatched >= this.watchPercentageThreshold &&
          this.totalUniqueSecondsWatched >= this.requiredUniqueSeconds &&
          !this.fastForwardDetected
        ) {
          this.hasWatched95Percent = true;
          if (this.debugMode && !this.hasWatched95Percent) {
            console.log("🎯 95% threshold reached with genuine watching");
          }
        }

        if (
          this.debugMode &&
          Math.floor(newTime) % 5 === 0 &&
          Math.floor(newTime) !== Math.floor(previousTime)
        ) {
          console.log("📊 Progress update:", {
            currentTime: Math.floor(newTime),
            percentWatched: Math.floor(this.percentWatched),
            uniqueSecondsWatched: this.totalUniqueSecondsWatched,
            requiredSeconds: this.requiredUniqueSeconds,
            seekingCount: this.seekingCount,
            eligibleForPoints: this.eligibleForPoints,
            watchedRanges: Array.from(this.watchedTimeRanges).sort(
              (a, b) => a - b
            )
          });
        }
      } catch (error) {
        if (this.debugMode) {
          console.error("❌ Error tracking YouTube progress:", error);
        }
      }
    },

    handleVideoEnded() {
      // First, check if user has already watched/earned points
      this.checkWatchStatus().then(() => {
        if (this.debugMode) {
          console.log("🏁 Video ended - checking eligibility:", {
            hasAlreadyWatched: this.hasAlreadyWatched,
            genuinelyWatched: this.genuinelyWatched,
            percentWatched: this.percentWatched,
            totalUniqueSecondsWatched: this.totalUniqueSecondsWatched,
            requiredUniqueSeconds: this.requiredUniqueSeconds,
            videoDuration: this.videoDuration,
            watchedRanges: Array.from(this.watchedTimeRanges).sort(
              (a, b) => a - b
            ),
            seekingCount: this.seekingCount,
            fastForwardDetected: this.fastForwardDetected,
            eligibleForPoints: this.eligibleForPoints
          });
        }

        // If user has already earned points, don't show any messages
        if (this.hasAlreadyWatched) {
          if (this.debugMode) {
            console.log("ℹ️ User has already watched this video");
          }
          return;
        }

        // Only award points if user has genuinely watched the video
        if (this.genuinelyWatched) {
          if (this.debugMode) {
            console.log("🎉 User eligible for points!");
          }

          // Show message and award points
          const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
          this.$store.dispatch("setSnackbar", message);

          // Add points to member account
          this.recordWatchCompletion().then(recorded => {
            if (recorded) {
              this.awardWatchPoints();
              if (this.debugMode) {
                console.log("✅ Points awarded successfully");
              }
            } else {
              if (this.debugMode) {
                console.log("❌ Failed to record watch completion");
              }
            }
          });
        } else if (
          this.percentWatched >= 95 &&
          this.totalUniqueSecondsWatched >= this.requiredUniqueSeconds * 0.7
        ) {
          // Fallback: If user watched 95% of video and at least 70% of required unique seconds
          if (this.debugMode) {
            console.log("🎉 User eligible for points via fallback condition!");
          }

          const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
          this.$store.dispatch("setSnackbar", message);

          this.recordWatchCompletion().then(recorded => {
            if (recorded) {
              this.awardWatchPoints();
              if (this.debugMode) {
                console.log("✅ Points awarded successfully (fallback)");
              }
            }
          });
        } else if (this.percentWatched >= 90) {
          // Additional fallback: If user watched 90%+ of the video, award points
          // This handles cases where time tracking might have issues
          if (this.debugMode) {
            console.log(
              "🎉 User eligible for points via high completion fallback (90%+)!"
            );
          }

          const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
          this.$store.dispatch("setSnackbar", message);

          this.recordWatchCompletion().then(recorded => {
            if (recorded) {
              this.awardWatchPoints();
              if (this.debugMode) {
                console.log(
                  "✅ Points awarded successfully (high completion fallback)"
                );
              }
            }
          });
        } else if (this.percentWatched >= 95) {
          // If they've reached 95% but through skipping
          if (this.debugMode) {
            console.log(
              "⚠️ User reached 95% but not eligible due to insufficient unique seconds"
            );
          }
          this.$store.dispatch(
            "setSnackbar",
            "Please watch the video without skipping to earn points."
          );
        } else {
          if (this.debugMode) {
            console.log("ℹ️ User did not watch enough of the video");
          }
        }
      });
    },

    setupVimeoTracking() {
      // Load Vimeo API if not already loaded
      if (!window.Vimeo) {
        const script = document.createElement("script");
        script.src = "https://player.vimeo.com/api/player.js";
        script.onload = this.initVimeoPlayer;
        document.head.appendChild(script);
      } else {
        this.initVimeoPlayer();
      }
    },

    initVimeoPlayer() {
      const iframe = document.getElementById("vimeo-player");
      if (!iframe) return;

      try {
        const player = new Vimeo.Player(iframe);

        player.getDuration().then(duration => {
          this.videoDuration = duration;
          // Reset validation state when video is ready to play
          this.resetValidationState();
        });

        // Track seeking events
        player.on("seeked", data => {
          this.seekingCount++;

          // Get previous time from watchLog or default to 0
          const previousTime =
            this.watchLog.length > 0
              ? this.watchLog[this.watchLog.length - 1].time
              : 0;

          // Calculate jump size
          const jumpSize = Math.abs(data.seconds - previousTime);

          // Record significant jumps
          if (jumpSize > 2) {
            this.timeJumps.push({
              from: previousTime,
              to: data.seconds,
              jumpSize: jumpSize,
              timestamp: Date.now()
            });

            // If seeking forward significantly, flag it
            if (data.seconds > previousTime && jumpSize > 10) {
              if (this.seekingCount > this.maxAllowedSeeks) {
                this.fastForwardDetected = true;
                this.eligibleForPoints = false;
              }
            }
          }
        });

        player.on("play", () => {
          // Check if this is a re-play from the beginning
          player.getCurrentTime().then(currentTime => {
            if (Math.floor(currentTime) <= 3) {
              // If user starts from beginning, reset validation
              this.resetValidationState();
            }
          });

          // Start tracking when playing
          this.vimeoTrackingInterval = setInterval(() => {
            player.getCurrentTime().then(currentTime => {
              const now = Date.now();
              const timeDiff = now - this.lastTimeUpdate;
              const previousTime = this.currentTime;

              // Track this second as watched
              this.trackUniqueTimeRange(currentTime);

              // Record the update
              this.watchLog.push({
                time: currentTime,
                timestamp: now,
                timeSinceLastUpdate: timeDiff
              });

              // Detect if user is fast-forwarding
              const expectedTimeDiff = timeDiff / 1000;
              const actualTimeDiff = currentTime - previousTime;

              const isNormalPlayback =
                actualTimeDiff <= expectedTimeDiff * 1.5 && actualTimeDiff >= 0;

              // If playback speed is much faster than real-time, flag as fast-forwarding
              if (actualTimeDiff > 0 && actualTimeDiff > expectedTimeDiff * 2) {
                this.timeJumps.push({
                  from: previousTime,
                  to: currentTime,
                  jumpSize: actualTimeDiff,
                  expectedDiff: expectedTimeDiff,
                  timestamp: now
                });

                // If we detect multiple fast-forward events, mark the user ineligible
                if (this.timeJumps.length > 3) {
                  this.fastForwardDetected = true;
                  this.eligibleForPoints = false;
                }
              }

              // Update continuous watching segments
              if (this.continuousWatchSegments.length === 0) {
                // First segment
                this.continuousWatchSegments.push({
                  start: currentTime,
                  end: currentTime
                });
              } else {
                // Update the end of the last segment if continuous
                const lastSegment = this.continuousWatchSegments[
                  this.continuousWatchSegments.length - 1
                ];

                if (
                  isNormalPlayback &&
                  Math.abs(currentTime - lastSegment.end) < 2
                ) {
                  lastSegment.end = currentTime;
                } else {
                  // Start a new segment
                  this.continuousWatchSegments.push({
                    start: currentTime,
                    end: currentTime
                  });
                }
              }

              // Update current time
              this.currentTime = currentTime;
              this.lastTimeUpdate = now;

              // Check if we've reached 95% with genuine watching
              if (
                this.percentWatched >= this.watchPercentageThreshold &&
                this.totalUniqueSecondsWatched >= this.requiredUniqueSeconds &&
                !this.fastForwardDetected
              ) {
                this.hasWatched95Percent = true;
              }
            });
          }, 1000);
        });

        player.on("pause", () => {
          // Clear interval when video is paused
          clearInterval(this.vimeoTrackingInterval);
        });

        player.on("ended", () => {
          // Clear interval when video ends
          clearInterval(this.vimeoTrackingInterval);

          // First, check if user has already watched/earned points
          this.checkWatchStatus().then(() => {
            // If user has already earned points, don't show any messages
            if (this.hasAlreadyWatched) {
              return;
            }

            // Only award points if user has genuinely watched the video
            if (this.genuinelyWatched) {
              // Show message and award points
              const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
              this.$store.dispatch("setSnackbar", message);

              // Add points to member account
              this.recordWatchCompletion().then(recorded => {
                if (recorded) {
                  this.awardWatchPoints();
                }
              });
            } else if (
              this.percentWatched >= 95 &&
              this.totalUniqueSecondsWatched >= this.requiredUniqueSeconds * 0.7
            ) {
              // Fallback: If user watched 95% of video and at least 70% of required unique seconds
              if (this.debugMode) {
                console.log(
                  "🎉 User eligible for points via fallback condition!"
                );
              }

              const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
              this.$store.dispatch("setSnackbar", message);

              this.recordWatchCompletion().then(recorded => {
                if (recorded) {
                  this.awardWatchPoints();
                  if (this.debugMode) {
                    console.log("✅ Points awarded successfully (fallback)");
                  }
                }
              });
            } else if (this.percentWatched >= 90) {
              // Additional fallback: If user watched 90%+ of the video, award points
              // This handles cases where time tracking might have issues
              if (this.debugMode) {
                console.log(
                  "🎉 User eligible for points via high completion fallback (90%+)!"
                );
              }

              const message = `Thanks for watching! You have been awarded ${this.article.watchPoints} points`;
              this.$store.dispatch("setSnackbar", message);

              this.recordWatchCompletion().then(recorded => {
                if (recorded) {
                  this.awardWatchPoints();
                  if (this.debugMode) {
                    console.log(
                      "✅ Points awarded successfully (high completion fallback)"
                    );
                  }
                }
              });
            } else if (this.percentWatched >= 95) {
              // If they've reached 95% but through skipping
              this.$store.dispatch(
                "setSnackbar",
                "Please watch the video without skipping to earn points."
              );
            }
          });
        });
      } catch (error) {
        // Handle error silently
      }
    },

    resetValidationState() {
      // Reset all validation flags to allow the user to earn points on a fresh viewing
      if (this.debugMode) {
        console.log("🔄 Resetting validation state");
      }

      this.hasWatched95Percent = false;
      this.seekingCount = 0;
      this.fastForwardDetected = false;
      this.timeJumps = [];
      this.continuousWatchSegments = [];
      this.watchedTimeRanges = new Set();
      this.eligibleForPoints = true;

      if (this.debugMode) {
        console.log("✅ Validation state reset complete");
      }
    },

    handleDialogChange(isOpen) {
      if (isOpen) {
        // Dialog is opening - setup video tracking
        if (this.debugMode) {
          console.log("📺 Dialog opened, setting up video tracking...");
        }

        // Add a delay to ensure the DOM is ready
        this.$nextTick(() => {
          setTimeout(() => {
            this.setupVideoTracking();
          }, 200);
        });
      } else {
        // Dialog is closing - cleanup
        if (this.debugMode) {
          console.log("🚪 Dialog closing, cleaning up...");
        }

        // Clear intervals
        if (this.youtubeTrackingInterval) {
          clearInterval(this.youtubeTrackingInterval);
          this.youtubeTrackingInterval = null;
        }

        if (this.vimeoTrackingInterval) {
          clearInterval(this.vimeoTrackingInterval);
          this.vimeoTrackingInterval = null;
        }

        // Clean up YouTube player
        if (this.youtubePlayer) {
          try {
            this.youtubePlayer.destroy();
          } catch (e) {
            // Ignore errors when destroying
          }
          this.youtubePlayer = null;
        }
      }
    },

    // Manual trigger for testing - call from console: $vm.manualVideoSetup()
    manualVideoSetup() {
      console.log("🔧 Manual video setup triggered");
      this.debugMode = true;
      this.resetTrackingData();
      this.setupVideoTracking();
    },

    // Manual trigger for testing video end - call from console: $vm.manualVideoEnd()
    manualVideoEnd() {
      console.log("🔧 Manual video end triggered");
      this.debugMode = true;
      this.handleVideoEnded();
    },

    // Manual trigger for testing video play - call from console: $vm.manualPlay()
    manualPlay() {
      console.log("🔧 Manual play triggered");
      this.debugMode = true;
      if (this.youtubePlayer && this.youtubePlayer.playVideo) {
        this.youtubePlayer.playVideo();
        console.log("▶️ YouTube player play() called");
      } else {
        console.log("❌ YouTube player not available");
      }
    },

    // Check iframe status - call from console: $vm.checkIframe()
    checkIframe() {
      const iframe = document.getElementById("youtube-player");
      console.log("🔍 Iframe status:", {
        exists: !!iframe,
        src: iframe?.src,
        width: iframe?.offsetWidth,
        height: iframe?.offsetHeight,
        visible: iframe?.offsetParent !== null
      });
    }
  }
};
</script>

<style scoped>
.video-dialog {
  max-width: 800px !important;
  margin: 0 !important;
  overflow: visible !important;
  padding: 0 !important;
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  display: block;
}

.video-player-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.video-player-container iframe,
.video-player-container video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.video-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
}

.close-button {
  position: absolute !important;
  top: 12px !important;
  right: 12px !important;
  z-index: 9999 !important;
  background-color: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}

.debug-indicator {
  position: absolute !important;
  top: 12px !important;
  left: 12px !important;
  z-index: 9999 !important;
}

/* Vuetify dialog overrides */
:deep(.v-dialog > .v-card) {
  padding: 0 !important;
  margin: 0 !important;
  background: #000 !important;
  display: block !important;
  overflow: hidden !important;
  border-radius: 4px !important;
  height: auto !important;
}

:deep(.v-dialog > .v-card > .v-card__text) {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
}

:deep(.vue-plyr) {
  display: block;
  width: 100%;
  height: 100%;
}

:deep(.plyr) {
  height: 100%;
}

:deep(.plyr__video-wrapper) {
  height: 100%;
}

:deep(.v-dialog > .v-card > .v-card__text > div) {
  height: 100% !important;
}

.incentable-container {
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
  background: #000 !important;
}

.incentable-video {
  object-fit: cover !important;
  object-position: center !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  display: block !important;
}

/* Style for specific video types */
video::-webkit-media-controls {
  display: flex !important;
  background: transparent !important;
}

video::-webkit-media-controls-enclosure {
  background: transparent !important;
}

/* Remove any default margin or padding */
* {
  box-sizing: border-box;
}

:deep(.plyr--youtube) {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

:deep(.plyr--youtube .plyr__video-wrapper) {
  height: 100% !important;
}

:deep(.plyr--youtube iframe) {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

:deep(.plyr--fullscreen-active) {
  background: #000 !important;
}

.plyr__video-embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-bottom: 0;
}

.plyr__video-embed iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
</style>
